/**
 * Meeting sound effects using Web Audio API.
 * Generates short, pleasant tones without requiring external audio files.
 */

export type SoundType = "join" | "request" | "screenshare";

let audioContext: AudioContext | null = null;
let soundEnabled = true;

/** Set whether sound effects are enabled */
export function setSoundEnabled(enabled: boolean): void {
  soundEnabled = enabled;
}

/** Check whether sound effects are enabled */
export function isSoundEnabled(): boolean {
  return soundEnabled;
}

function getAudioContext(): AudioContext {
  if (!audioContext || audioContext.state === "closed") {
    audioContext = new AudioContext();
  }
  return audioContext;
}

/**
 * Play a meeting sound effect.
 * Silently fails if audio is unavailable (e.g., before user gesture).
 * Respects the soundEnabled setting — no-ops when disabled.
 */
export function playSound(type: SoundType): void {
  if (!soundEnabled) return;

  try {
    const ctx = getAudioContext();
    if (ctx.state === "suspended") {
      ctx.resume();
    }

    switch (type) {
      case "join":
        playJoinSound(ctx);
        break;
      case "request":
        playRequestSound(ctx);
        break;
      case "screenshare":
        playScreenShareSound(ctx);
        break;
    }
  } catch {
    // Non-critical — silently ignore audio errors
  }
}

/** Short ascending two-note chime for participant joining */
function playJoinSound(ctx: AudioContext): void {
  const now = ctx.currentTime;

  // First note: C5
  const osc1 = ctx.createOscillator();
  const gain1 = ctx.createGain();
  osc1.connect(gain1);
  gain1.connect(ctx.destination);
  osc1.type = "sine";
  osc1.frequency.setValueAtTime(523.25, now);
  gain1.gain.setValueAtTime(0.25, now);
  gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
  osc1.start(now);
  osc1.stop(now + 0.15);

  // Second note: E5
  const osc2 = ctx.createOscillator();
  const gain2 = ctx.createGain();
  osc2.connect(gain2);
  gain2.connect(ctx.destination);
  osc2.type = "sine";
  osc2.frequency.setValueAtTime(659.25, now + 0.12);
  gain2.gain.setValueAtTime(0.25, now + 0.12);
  gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.35);
  osc2.start(now + 0.12);
  osc2.stop(now + 0.35);
}

/** Double-beep notification for join requests */
function playRequestSound(ctx: AudioContext): void {
  const now = ctx.currentTime;

  [0, 0.18].forEach((delay) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, now + delay);
    gain.gain.setValueAtTime(0.25, now + delay);
    gain.gain.exponentialRampToValueAtTime(0.01, now + delay + 0.12);
    osc.start(now + delay);
    osc.stop(now + delay + 0.12);
  });
}

/** Soft ascending two-note chime for screen share start */
function playScreenShareSound(ctx: AudioContext): void {
  const now = ctx.currentTime;

  // Keep the notes at a fixed pitch; the previous downward pitch sweep sounded
  // more like an alert than the other meeting chimes.
  [659.25, 783.99].forEach((frequency, index) => {
    const startAt = now + index * 0.13;
    const endAt = startAt + 0.22;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.setValueAtTime(frequency, startAt);
    gain.gain.setValueAtTime(0.14, startAt);
    gain.gain.exponentialRampToValueAtTime(0.01, endAt);
    osc.start(startAt);
    osc.stop(endAt);
  });
}
